import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import QuickActions from "./QuickActions";

const ease = [0.16, 1, 0.3, 1];

export default function ContactCTA() {
  const { t, lang } = useLang();

  return (
    <section
      id="contact"
      data-testid="contact-cta"
      className="relative bg-[#0A0A0A] text-[#F7F7F9] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #F7F7F9 1px, transparent 1px), linear-gradient(to bottom, #F7F7F9 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-36 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-8 h-px bg-[#F7F7F9]" />
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#F7F7F9]/70">
            {t.contact.overline} / 05
          </span>
        </motion.div>

        <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="col-span-12 lg:col-span-8"
          >
            <h2 className="font-display font-light uppercase tracking-[-0.02em] leading-[0.95] text-[clamp(2.5rem,7vw,7rem)]">
              {lang === "fr" ? (
                <>
                  Démarrons<br />
                  <span className="text-[#F7F7F9]/60">votre projet</span>
                  <span className="text-[#D35400]">.</span>
                </>
              ) : (
                <>
                  Start<br />
                  <span className="text-[#F7F7F9]/60">your project</span>
                  <span className="text-[#D35400]">.</span>
                </>
              )}
            </h2>

            <p className="mt-8 max-w-[58ch] text-base md:text-lg font-light leading-relaxed text-[#F7F7F9]/85">
              {lang === "fr"
                ? "Vous avez un projet de bureaux, d'aménagement, de rénovation ou un besoin en BIM / Revit ? Contactez-moi pour cadrer votre demande et recevoir un devis professionnel."
                : "Office layout, fit-out, renovation or a BIM / Revit need? Get in touch — I'll help frame your request and send a professional quote."}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
            className="col-span-12 lg:col-span-4 space-y-8"
          >
            <QuickActions variant="footer" source="contact-cta" className="!gap-2" />

            <Link
              to="/contact"
              data-testid="contact-cta-button"
              className="group inline-flex items-center gap-4 border border-[#F7F7F9] text-[#F7F7F9] px-8 py-5 text-sm tracking-[0.18em] uppercase font-mono hover:bg-[#D35400] hover:border-[#D35400] transition-colors duration-500 w-full justify-between"
            >
              {lang === "fr" ? "Ouvrir le formulaire complet" : "Open full form"}
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-500" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
